import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { DatatableComponent, ColumnMode } from '@swimlane/ngx-datatable';
import { RastreadorService } from 'app/services/rastreador.service';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-rastreador-list',
  templateUrl: './rastreador-list.component.html',
  styleUrls: ['./rastreador-list.component.scss']
})
export class RastreadorListComponent implements OnInit, OnDestroy {
  
  // Private variables
  private _unsubscribeAll: Subject<any> = new Subject();
  private searchSubject = new Subject<void>();
  private searchSubscription: Subscription;
  
  // Public variables
  public loading: boolean = false;
  public ColumnMode = ColumnMode;
  public rows: any[] = [];
  public basicSelectedOption: number = 10;
  public contentHeader: any;
  private tempData = [];
  public temp = [];
  public selectedStatus: any;
  activeTab: string = 'lista'; // Default active tab
  lotes: any[] = []; // Array to store lotes data
  public currentPage: number = 1;
  public itemsPerPage: number = 10;
  public totalItems: number = 0;
  public totalPages: number = 0;

  @ViewChild(DatatableComponent) table: DatatableComponent;

  private searchParams = {
    imei: '',
    modelo: '',
    estoque: '',
    status: 0
  };

  filters = {
    dt_compra: '',
    dt_chegada: '',
    modelo_rastreador: '',
    nome_despachante: '',
    nome_fabricante: '',
    nome_fornecedor: '',
    total_rastreadores: ''
  };

  filteredLotes: any[] = [];

  constructor(
    private rastreadorService: RastreadorService,
    private router: Router
  ) { }

  ngOnInit() {
    this.loading = true;
    this.loadData();
    
    // Configuração do cabeçalho
    this.contentHeader = {
      headerTitle: "Gerenciamento de Rastreadores",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          { name: "Início", isLink: false },
          { name: "Rastreadores", isLink: false }
        ]
      }
    };

    // Configura o debounce para a pesquisa
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(1000),
      switchMap(() => {
        return this.searchData().finally(() => {
          // Desativar o spinner após a pesquisa
          this.loading = false;
        });
      })
    ).subscribe();
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  async loadData() {
    try {
      const result = await this.rastreadorService.getPaginated(this.currentPage, this.itemsPerPage);
      this.rows = Array.isArray(result.data) ? result.data : [];
      this.temp = [...this.rows];
      this.totalItems = result.meta.total;
      this.totalPages = result.meta.totalPages;

      // Load lotes data
      const lotesResponse = await this.rastreadorService.listarLoteEntradaRastreador({});
      this.lotes = Array.isArray(lotesResponse?.data) ? lotesResponse.data : [];
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      this.rows = [];
      this.temp = [];
      this.lotes = [];
    } finally {
      this.loading = false;
    }
  }

  async onPageChange(event: any) {
    this.currentPage = event.offset + 1;
    this.itemsPerPage = event.pageSize;
    await this.loadData();
  }

  onSearch() {
    this.searchSubject.next();
  }

  async searchData() {
    try {
      const result = await this.rastreadorService.search(
        this.searchParams.imei || undefined,
        this.searchParams.modelo || undefined,
        this.searchParams.estoque || undefined,
        this.searchParams.status || undefined,
        this.currentPage,
        this.itemsPerPage
      );
      this.rows = Array.isArray(result.data) ? result.data : [];
      this.temp = [...this.rows];
      this.totalItems = result.meta.total;
      this.totalPages = result.meta.totalPages;
    } catch (error) {
      console.error('Erro ao pesquisar:', error);
      this.rows = [];
      this.temp = [];
    }
  }

  clearFilters() {
    this.searchParams = {
      imei: '',
      modelo: '',
      estoque: '',
      status: 0
    };
    this.currentPage = 1;
    this.loadData();
  }

  // Add filter methods
  nomeFilter(event) {
    const val = event.target.value.toLowerCase();

    const temp = this.temp.filter(function (d) {
      if (d.nome != null) {
        return d.nome.toLowerCase().indexOf(val) !== -1 || !val;
      }
    });

    this.rows = temp;
    this.table.offset = 0;
  }

  imeiFilter(event: any) {
    // Ativar o spinner imediatamente
    this.loading = true;
    
    // Atualizar o parâmetro de pesquisa
    this.searchParams.imei = event.target.value;
    
    // Emitir o evento para o subject
    this.searchSubject.next();
  }

  modeloFilter(event: any) {
    this.searchParams.modelo = event.target.value;
    this.searchSubject.next();
  }

  statusFilter() {
    this.searchParams.status = this.selectedStatus;
    this.searchSubject.next();
  }

  estoqueFilter(event: any) {
    this.searchParams.estoque = event.target.value;
    this.searchSubject.next();
  }

  onEdit(id_rastreador) {
    this.router.navigate(['rastreador/editar/', id_rastreador]);
  }

  async onDelete(id_rastreador: number) {
    const result = await Swal.fire({
      title: 'Tem certeza?',
      text: "Esta ação não poderá ser revertida!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        this.loading = true;
        await this.rastreadorService.delete(id_rastreador);
        
        this.rows = this.rows.filter(row => row.id_rastreador !== id_rastreador);
        this.temp = this.temp.filter(row => row.id_rastreador !== id_rastreador);
        
        Swal.fire(
          'Deletado!',
          'O rastreador foi deletado com sucesso.',
          'success'
        );
      } catch (error) {
        console.error('Erro ao deletar rastreador:', error);
        Swal.fire(
          'Erro!',
          'Ocorreu um erro ao deletar o rastreador.',
          'error'
        );
      } finally {
        this.loading = false;
      }
    }
  }

  public onCreate(): void {
    this.router.navigate(['rastreador/cadastro']);
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  onCreateLote() {
    // Implement create lote functionality
  }

  onEditLote(id: number) {
    // Implement edit lote functionality
  }

  onDeleteLote(id: number) {
    // Implement delete lote functionality
  }

  onViewLote(id: number) {
    // Implement your view logic here
    console.log('Visualizando lote:', id);
    // Example: this.router.navigate(['/rastreador-entrada/visualizar', id]);
  }

  filterTable() {
    this.filteredLotes = this.lotes.filter(lote => {
      return (!this.filters.dt_compra || lote.dt_compra === this.filters.dt_compra) &&
             (!this.filters.dt_chegada || lote.dt_chegada === this.filters.dt_chegada) &&
             (!this.filters.modelo_rastreador || lote.modelo_rastreador.toLowerCase().includes(this.filters.modelo_rastreador.toLowerCase())) &&
             (!this.filters.nome_despachante || lote.nome_despachante.toLowerCase().includes(this.filters.nome_despachante.toLowerCase())) &&
             (!this.filters.nome_fabricante || lote.nome_fabricante.toLowerCase().includes(this.filters.nome_fabricante.toLowerCase())) &&
             (!this.filters.nome_fornecedor || lote.nome_fornecedor.toLowerCase().includes(this.filters.nome_fornecedor.toLowerCase())) &&
             (!this.filters.total_rastreadores || lote.total_rastreadores.toString().includes(this.filters.total_rastreadores));
    });
  }
}
